import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { LoadingSpinner } from '../components';
import { courseService } from '../services/courseService';
import { topicService } from '../services/topicService';
import '../styles/CourseDetailPage.css';

// Utility function to format file size
const formatFileSize = (bytes) => {
  if (!bytes) return '';
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(1024));
  return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
};

const CourseDetailsPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const [course, setCourse] = useState(null);
  const [topics, setTopics] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedSubjects, setExpandedSubjects] = useState({});



  useEffect(() => {
    const fetchCourseData = async () => {
      if (!courseId) return;

      try {
        setLoading(true);

        // Fetch course details and topics in parallel
        const [courseResponse, topicsResponse] = await Promise.all([
          courseService.getCourseDetails(courseId),
          topicService.getTopicsByCourseId(courseId)
        ]);

        if (courseResponse && courseResponse.data) {
          console.log('Full Course API Response:', courseResponse);
          console.log('courseResponse.data type:', typeof courseResponse.data);
          console.log('courseResponse.data is array:', Array.isArray(courseResponse.data));

          let courseData = null;

          if (Array.isArray(courseResponse.data) && courseResponse.data.length > 0) {
            // Response.data is directly an array of courses
            courseData = courseResponse.data[0];
            console.log('✅ Course data extracted from direct array:', courseData);
          } else if (courseResponse.data.data && Array.isArray(courseResponse.data.data) && courseResponse.data.data.length > 0) {
            // Response has nested data: { data: { data: [courseObject] } }
            courseData = courseResponse.data.data[0];
            console.log('✅ Course data extracted from nested array:', courseData);
          } else {
            console.log('❌ Unexpected response structure:', courseResponse.data);
            courseData = courseResponse.data;
          }

          console.log('🎯 Final Course Data to set:', courseData);
          console.log('🎯 Course title:', courseData?.title);
          console.log('🎯 Course description:', courseData?.description);
          console.log('🎯 Course image:', courseData?.displayPicture);

          setCourse(courseData);
        }

        // Handle topics response - check for nested data structure
        if (topicsResponse && topicsResponse.data) {
          // Check if data is nested (response.data.data) or direct (response.data)
          const topicsData = Array.isArray(topicsResponse.data.data)
            ? topicsResponse.data.data
            : Array.isArray(topicsResponse.data)
              ? topicsResponse.data
              : [];
          setTopics(topicsData);
        }

      } catch (error) {
        console.error('Error fetching course data:', error);
        toast.error('Failed to load course details. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    fetchCourseData();
  }, [courseId]);

  const handleBack = () => {
    navigate('/dashboard');
  };

  const toggleSubject = (subjectId) => {
    setExpandedSubjects(prev => ({
      ...prev,
      [subjectId]: !prev[subjectId]
    }));
  };



  const handleReadTopic = (topic) => {
    // Navigate to reading page with topic data
    navigate('/reading', {
      state: {
        topic: topic,
        courseId: courseId
      }
    });
  };









  if (loading) {
    return (
      <div className="loadingContainer">
        <LoadingSpinner />
        <p>Loading course details...</p>
      </div>
    );
  }

  if (!course) {
    return (
      <div className="errorContainer">
        <p className="errorText">Course not found</p>
        <p className="body3 debugText">
          Debug: courseId = {courseId}, loading = {loading.toString()}
        </p>
        <button className="backButton" onClick={handleBack}>
          BACK
        </button>
      </div>
    );
  }

  // Debug log for render
  console.log('🎨 Rendering CourseDetailsPage with course:', course);

  return (
    <div className="container">
      {/* Header with course image, title and back button */}
      <div className="header">
        <div className="courseInfo">
          {course.displayPicture && (
            <div className="courseImageContainer">
              <img
                src={course.displayPicture}
                alt={course.title}
                className="courseImage"
                onError={(e) => {
                  console.log('❌ Image failed to load:', course.displayPicture);
                  e.target.style.display = 'none';
                }}
                onLoad={() => {
                  console.log('✅ Image loaded successfully:', course.displayPicture);
                }}
              />
            </div>
          )}
          <div className="titleSection">
            <h1 className="h2 courseTitle">{course.title}</h1>
            <div className="underline"></div>
            {/* Show course metadata */}
            <div className="courseMetadata">
              <span className="body3 coursePrice">
                ₹{course.offerPrice} {course.originalPrice && course.originalPrice !== course.offerPrice && (
                  <span className="originalPrice">₹{course.originalPrice}</span>
                )}
              </span>
              {course.tags && (
                <div className="courseTags">
                  {course.tags.split(',').slice(0, 3).map((tag, index) => (
                    <span key={index} className="courseTag">{tag.trim()}</span>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
        <button className="backButton" onClick={handleBack}>
          BACK
        </button>
      </div>

      {/* Topics Section */}
      <div className="topicsSection">
        <div className="curriculumContent">
          <h2 className="h3 sectionTitle">Topics</h2>
          <div className="topicsList">
            {topics.map((topic, index) => (
              <div key={topic.id} className="topicCard">
                <div
                  className="chapterItem"
                  onClick={() => handleReadTopic(topic)}
                >
                  <div className="topicInfo">
                    <span className="chapterNumber">Chapter : {index + 1}</span>
                    <span className="chapterTitle">{topic.title}</span>
                  </div>
                  <span className="readIcon">
                    📖
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CourseDetailsPage;